"use client";

import Link from "next/link";
import { useMemo } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Camera,
  Gift,
  ImageIcon,
  MapPin,
  QrCode,
} from "lucide-react";
import { IPhoneMockup } from "@/components/IPhoneMockup";
import { getScanShowcaseImagePath } from "@/lib/home-showcase-images";
import { useHomeShowcaseImageTheme } from "@/lib/useHomeShowcaseImageTheme";
import {
  HOME_RESTAURANT_PREVIEW_BENEFIT_IDS,
  getHomeRestaurantPreviewFeatures,
} from "@/lib/restaurant-pricing-features";
import { useLanguage } from "@/lib/useLanguage";

const FEATURE_ICONS = [MapPin, Camera, QrCode, ImageIcon, BadgeCheck, Gift] as const;

export function HomeRestaurantPreviewSection() {
  const { language, t } = useLanguage();
  const imageTheme = useHomeShowcaseImageTheme();
  const copy = t.homePage.restaurants;
  const chips = t.restaurantsPage.hero.chips;

  const heroChips = useMemo(
    () => [chips.noCommissions, chips.planMonthlyAnnual, chips.platforms, chips.availableAcrossSpain],
    [chips]
  );

  const previewFeatures = useMemo(
    () => getHomeRestaurantPreviewFeatures(t.restaurantsPage.pricing.features),
    [t.restaurantsPage.pricing.features]
  );

  return (
    <section
      id="para-restaurantes"
      className="mb-14 lg:mb-16 scroll-mt-20"
      aria-labelledby="para-restaurantes-heading"
    >
      <div className="rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800/80 dark:to-slate-900/80 border border-slate-200 dark:border-slate-700/50 p-6 sm:p-8 lg:p-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-2">
              {copy.eyebrow}
            </p>
            <h2
              id="para-restaurantes-heading"
              className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-3 leading-tight"
            >
              {copy.headline}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base mb-4 max-w-lg leading-relaxed">
              {copy.subtitle}
            </p>

            <ul className="flex flex-wrap gap-2 mb-5">
              {heroChips.map((chip) => (
                <li
                  key={chip}
                  className="inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-medium border-slate-200 bg-white/80 text-slate-700 dark:border-slate-700/60 dark:bg-slate-800/50 dark:text-slate-300"
                >
                  {chip}
                </li>
              ))}
            </ul>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
              {previewFeatures.map((feature, index) => {
                const Icon = FEATURE_ICONS[index] ?? BadgeCheck;
                const benefitId = HOME_RESTAURANT_PREVIEW_BENEFIT_IDS[index];
                return (
                  <li
                    key={benefitId}
                    className="flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs sm:text-sm leading-snug border-slate-200/80 bg-white/60 text-slate-700 dark:border-slate-700/50 dark:bg-slate-900/30 dark:text-slate-300"
                  >
                    <Icon
                      className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400"
                      aria-hidden
                    />
                    <span>{feature}</span>
                  </li>
                );
              })}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <Link
                href="/download"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
              >
                {copy.primaryCta}
                <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
              </Link>
              <Link
                href="/restaurants"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
              >
                {copy.secondaryCta}
              </Link>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed max-w-lg">
              {copy.claimHint}
            </p>
          </div>

          <div className="flex justify-center lg:justify-end">
            <IPhoneMockup
              key={`${language}-${imageTheme}`}
              src={getScanShowcaseImagePath("menu", language, imageTheme)}
              alt={copy.menuAlt}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
